import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ENavigationRoute } from '../models/enum/navigation-route';
import { ENavigationErrorType } from '../models/navigation-error-type';
import { CoreData } from '../tools/core-data';
import { LoadingTask } from '../tools/loading-task';
import { CustomSnackBarComponent } from '../tools/ui/custom-snackbar/custom-snackbar.component';
import { Message } from '../tools/ui/custom-snackbar/model/message';
import { MessageSeverity } from '../tools/ui/custom-snackbar/model/message-severity';
import { MatSnackBar } from '@angular/material/snack-bar';
const { version: appVersion } = require('../../../package.json');

export class BaseService {

    constructor(
        protected coreData: CoreData,
        protected router: Router,
        protected snackBar: MatSnackBar,
        protected http?: HttpClient,
        protected loadingTask?: LoadingTask
    ) {
    }

    protected getFingerPrintGuid() {
        const nav = window.navigator;
        // const screen = window.window.screen;
        let guid = nav.mimeTypes.length + '';
        guid += nav.userAgent.replace(/\D+/g, '');
        guid += nav.plugins.length;
        // guid += screen.height || '';
        // guid += screen.width || '';
        guid += screen.pixelDepth || '';

        return guid;
    }

    // private get BasicAuthorization() {
    //     return btoa((this.coreData.UserInformation ? this.coreData.UserInformation.ServerKey : '') + ':' + this.getFingerPrintGuid());
    // }

    private get BearerAuthorization() {
        return 'Bearer ' + this.coreData.Token;
    }

    protected generateHeaderWithPartnerCode(partnerCode: string) {

        const headers: HttpHeaders = new HttpHeaders({
            'Version': appVersion,
            'Accept': 'application/json',
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            'Content-Type': 'application/json',
            'Authorization': this.BearerAuthorization,
            'partnerCode': partnerCode
        });
        const options = { withCredentials: true, headers: headers };
        return options;
    }

    protected generateHeader() {

        const headers: HttpHeaders = new HttpHeaders({
            'Version': appVersion,
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': this.BearerAuthorization
        });
        const options = { withCredentials: true, headers: headers };
        return options;
    }

    protected generateHeaderWithParameters(params: HttpParams) {

        const headers: HttpHeaders = new HttpHeaders({
            'Version': appVersion,
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': this.BearerAuthorization
        });
        const options = { withCredentials: true, headers: headers, params };
        return options;
    }

    protected generateHeaderForFormData() {

        const headers: HttpHeaders = new HttpHeaders({
            'Version': appVersion,
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            'Accept': 'application/json',
            'Authorization': this.BearerAuthorization,
            'ngsw-bypass': 'true'
        });
        const options = { withCredentials: true, headers: headers, reportProgress: true };
        return options;
    }

    protected generateHeaderForFileDownload() {

        const headers: HttpHeaders = new HttpHeaders({
            'Version': appVersion,
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            'Authorization': this.BearerAuthorization
        });
        const options = { withCredentials: true, headers: headers, responseType: 'blob' as 'blob', observe: 'response' as 'body' };
        return options;
    }

    /**
     * Handle Http operation that failed.
     * Let the app continue.
     * @param operation - name of the operation that failed
     * @param result - optional value to return as the observable result
     */
    protected handleError<T>(operation = 'operation', result?: T) {
        return async (error: any): Promise<Observable<T>> => {
            let msg;
            switch (error.status) {
                case 400:
                    let txt = 'Mauvaise Action';
                    if (error.error && error.error.length > 0) {
                        txt = `${_.join(error.error, '\n')}`;
                    }
                    msg = new Message(txt, MessageSeverity.Error);
                    break;
                case 401:
                    if (this.coreData.RefreshToken) {
                        const newToken = await this.refreshToken(this.coreData.RefreshToken).toPromise()
                        if (newToken && typeof (newToken) === 'string') {
                            this.coreData.Token = newToken;
                            break;
                        }
                    }
                    msg = new Message('Puid ou mot de passe incorrect', MessageSeverity.Warning);
                    this.coreData.clearToken();
                    this.router.navigate([ENavigationRoute.Error, { type: ENavigationErrorType.UnAuthenticated }]);

                    break;
                case 404:
                    msg = new Message('Aucune donnée trouvée', MessageSeverity.Error);
                    break;
                case 426:
                    msg = new Message('Version obsolète', MessageSeverity.Error);
                    this.router.navigate([ENavigationRoute.Error, { type: ENavigationErrorType.DeprecatedVersion }]);
                    break;
                case 429:
                    msg = new Message('Trop de requêtes ont été envoyées. Vous devez attendre ' + error.error, MessageSeverity.Error);
                    break;
                case 500:
                    msg = new Message('Ouppsss le serveur n\'a pas su gérer votre demande ', MessageSeverity.Error);
                    break;
                case 0:
                    msg = new Message('Pas de réseau', MessageSeverity.Error);
                    break;
                default:
                    msg = new Message(error.message, MessageSeverity.Error);
            }

            if (this.snackBar) {
                this.snackBar.openFromComponent(CustomSnackBarComponent, { data: msg, horizontalPosition: 'center' });
            }

            console.error(error); // log to console instead
            throw error;
            // Let the app keep running by returning an empty result.
            return of(result as T);
        };
    }

    refreshToken(refreshToken: string) {

        return this.http.post(`${environment.serviceMainBaseUrl}api/user/refreshtoken/${refreshToken}`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('refreshToken')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }
}
